// pages/admin/program/ProgramSessions.tsx
import React, { useState, useEffect } from 'react';
import { programService, Program } from '../../../firebase/programFirestoreService';
import { FaCalendarCheck, FaUsers, FaCheckCircle } from '../../../utils/iconUtils';

const ProgramSessions: React.FC = () => {
  const [sessions, setSessions] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const data = await programService.getPrograms();
      // Filter for published/ongoing programs
      const activeSessions = data.filter(p => 
        p.isPublished && (p.status === 'published' || p.status === 'ongoing')
      );
      setSessions(activeSessions);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Program Sessions</h2>
          <p className="text-gray-600 text-sm">Active and upcoming program sessions</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <FaCalendarCheck className="mx-auto text-3xl text-gray-400 mb-3" />
          <h3 className="text-base font-medium text-gray-900 mb-1">No Active Sessions</h3>
          <p className="text-gray-600">There are no active program sessions at the moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sessions.map((session) => (
            <div key={session.id} className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-start mb-4">
                <h3 className="font-semibold text-gray-800">{session.title}</h3>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  session.status === 'ongoing' 
                    ? 'bg-green-100 text-green-800'
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {session.status}
                </span>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <FaCalendarCheck className="text-gray-400 h-4 w-4" />
                  <span className="text-gray-700">
                    {session.startDate?.toDate().toLocaleDateString()} - 
                    {session.endDate?.toDate().toLocaleDateString()}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <FaUsers className="text-gray-400 h-4 w-4" />
                  <span className="text-gray-700">
                    {session.registeredCount} registrations
                  </span>
                </div>
                
                {session.location && (
                  <div className="text-gray-600">
                    📍 {session.location}
                  </div>
                )}
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    Registration {session.requiresRegistration ? 'Required' : 'Open'}
                  </span>
                  {session.capacity && (
                    <div className="text-sm">
                      <span className="text-gray-600">Capacity: </span>
                      <span className="font-medium">{session.capacity}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProgramSessions;